"use client"
import { useState } from 'react'
import { useTranslations } from 'next-intl'
import type { Post } from '@/lib/api'

type Props = {
  locale: 'fr' | 'es'
  initialOffset: number
  onLoaded: (posts: Post[]) => void
}

export default function LoadMoreButton({ locale, initialOffset, onLoaded }: Props) {
  const t = useTranslations('ui')
  const [loading, setLoading] = useState(false)
  const [offset, setOffset] = useState(initialOffset)
  const [hasMore, setHasMore] = useState(true)
  const [error, setError] = useState<string | null>(null)

  async function handleClick() {
    if (loading || !hasMore) return
    
    setLoading(true)
    setError(null)
    
    try {
      // Configuration automatique de l'API (même logique que api-client.ts)
      const isDevelopment = process.env.NODE_ENV === 'development'
      const isLocalhost = typeof window !== 'undefined' && window.location.hostname === 'localhost'
      const API_BASE = process.env.NEXT_PUBLIC_API_BASE_URL || 
        (isDevelopment || isLocalhost ? 'http://localhost:8000' : 'https://msintapujos-backend.onrender.com')
      
      const url = `${API_BASE}/api/posts/?limit=10&offset=${offset}&locale=${locale}`
      const res = await fetch(url)
      
      if (res.ok) {
        const data = (await res.json()) as { results: Post[]; count: number }
        
        if (data.results.length === 0 || offset + 10 >= data.count) {
          setHasMore(false)
        }
        
        onLoaded(data.results)
        setOffset((o) => o + 10)
      } else {
        throw new Error(`Erreur ${res.status}: ${res.statusText}`)
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erreur de chargement')
      console.error('Erreur lors du chargement:', err)
    } finally {
      setLoading(false)
    }
  }

  if (!hasMore) {
    return (
      <div className="mt-8 text-center">
        <p className="text-gray-500 text-sm">
          📄 Tous les articles ont été chargés
        </p>
      </div>
    )
  }

  return (
    <div className="mt-8 text-center">
      {error && (
        <p className="text-red-600 text-sm mb-4 p-3 bg-red-50 rounded-lg">
          ⚠️ {error}
        </p>
      )}
      
      <button
        type="button"
        onClick={handleClick}
        disabled={loading}
        className="inline-flex items-center gap-2 px-6 py-3 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 disabled:opacity-60 disabled:cursor-not-allowed transition-colors duration-200"
      >
        {loading ? (
          <>
            <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            Chargement...
          </>
        ) : (
          <>
            <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 13l-7 7-7-7m14-8l-7 7-7-7" />
            </svg>
            {t('loadMore')}
          </>
        )}
      </button>
    </div>
  )
}

